import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { fetchDataCatalogs } from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import type { Component, DataCatalog } from "../../lib/api/products";

export function ComponentViewModal({
  open,
  onClose,
  accessToken,
  component,
}: {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  component: Component | null;
}) {
  const { t, tx } = useI18n();
  const [dataCatalogs, setDataCatalogs] = React.useState<DataCatalog[]>([]);

  React.useEffect(() => {
    if (!open || !accessToken) return;
    fetchDataCatalogs(accessToken)
      .then((all) => setDataCatalogs(all.filter((c) => c.isActive)))
      .catch(() => setDataCatalogs([]));
  }, [open, accessToken]);

  if (!component) return null;

  const catalogName =
    dataCatalogs.find((c) => c.id === component.dataCatalogId)?.name ??
    (component.dataCatalogId ? component.dataCatalogId : "— Use default for type —");

  const display = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
  };

  const field = (label: React.ReactNode, value: string) => (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "#6B7280",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );

  const title =
    component.name?.trim() ||
    component.componentType.toUpperCase() ||
    "Component";

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }} minWidth="480px" maxWidth="560px">
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 4 }}>
        <Text level="h3" weight="bold" style={{ margin: 0 }}>
          {title}
        </Text>

        {field(tx("pro.components.componentTypeRequired"), component.componentType.toUpperCase())}

        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
          {field(t("pro.components.displayName"), display(component.name))}
          {field(t("pro.components.index"), display(component.componentIndex))}
        </div>

        {field(t("pro.components.iotComponentId"), display(component.iotComponentId))}
        {field(tx("pro.components.dataCatalog"), catalogName)}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {field(t("pro.products.serialNumber"), display(component.serial))}
          {field(t("pro.products.macAddress"), display(component.macAddress))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {field(t("pro.components.manufacturer"), display(component.manufacturer))}
          {field(t("pro.products.model"), display(component.model))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {field(t("pro.components.hwVersion"), display(component.hwVersion))}
          {field(t("pro.components.fwVersion"), display(component.fwVersion))}
        </div>

        {field(
          "Active (include in telemetry polling)",
          component.isActive ? "Yes" : "No",
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
          <Button type="button" color="primary" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
